"use client";

import * as React from "react";
import { Funnel as Filter, Grid3x3, List } from 'lucide-react';
import {
  AddToCartButton,
  EcommerceProductsProvider,
  ProductSortDropdown,
  ProductsFilter,
  useEcommerceProducts,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: {
  getCurrentWPQueryParams: () => Record<string, any>;
};

const current_wp_query_params =
  typeof wvcClient !== "undefined" ? wvcClient.getCurrentWPQueryParams() : {};
const wp_query = new WP_Query(current_wp_query_params);

function ProductCard({
  product,
  index,
  spotlight = false,
}: {
  product: any;
  index: number;
  spotlight?: boolean;
}) {
  const modelCode = `LSH-${String(product.id).padStart(4, "0")}`;
  const specs = [
    product.categories?.[0]?.name || "APPAREL",
    "PREMIUM",
    "EDITORIAL",
  ];

  if (spotlight) {
    return (
      <article
        data-index={index}
        className="col-span-full border border-iron bg-alabaster shadow-lg group relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 px-4 py-2 bg-onyx text-alabaster font-mono text-xs tracking-widest z-10">
          § {String(index + 1).padStart(2, "0")} / SPOTLIGHT
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
          <Link to={product.link || "#"} className="block">
            <div className="relative aspect-square overflow-hidden bg-platinum">
              {product.featured_image_url ? (
                <img
                  src={product.featured_image_url}
                  alt={product.title}
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              ) : (
                <div className="h-full w-full bg-gradient-to-br from-platinum to-alabaster" />
              )}
            </div>
          </Link>
          <div className="p-8 lg:p-12 flex flex-col justify-between">
            <div>
              <div className="font-mono text-xs tracking-widest text-graphite mb-4">
                {modelCode} — SEASONAL ESSENTIAL
              </div>
              <Link to={product.link || "#"}>
                <h3 className="font-default text-3xl lg:text-4xl font-bold mb-4 leading-tight hover:text-onyx transition-colors">
                  {product.title}
                </h3>
              </Link>
              {product.short_description && (
                <div
                  className="font-default text-base text-graphite leading-relaxed mb-6 [&_p]:mb-2"
                  dangerouslySetInnerHTML={{ __html: product.short_description }}
                />
              )}
              <div className="grid grid-cols-3 gap-2 mb-8 border-t border-b border-platinum py-4">
                {specs.map((spec, i) => (
                  <div key={i} data-index={i} className="font-mono text-xs tracking-widest text-graphite">
                    {spec.toUpperCase()}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="font-mono text-xs tracking-widest text-graphite mb-1">PRICE</div>
                <div className="text-3xl font-bold text-onyx font-mono">
                  ${product.price?.toFixed(2) || "0.00"}
                </div>
              </div>
              <AddToCartButton
                productId={product.id}
                className="w-auto"
                buttonClassName="bg-onyx text-alabaster rounded-none hover:shadow-[4px_4px_0px_0px_var(--accent)] transition-all"
                text="ADD TO CART +"
                showQuantity={false}
              />
            </div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      data-index={index}
      className="group relative border border-platinum bg-alabaster hover:border-onyx transition-all duration-300"
    >
      <div className="absolute top-3 left-3 z-10 font-mono text-xs tracking-widest text-graphite">
        § {String(index + 1).padStart(2, "0")}
      </div>
      <Badge className="absolute top-3 right-3 z-10 bg-accent text-onyx border-none font-mono text-[10px] tracking-widest">
        NEW
      </Badge>
      <Link to={product.link || "#"} className="block">
        <div className="relative aspect-[3/4] overflow-hidden bg-platinum">
          {product.featured_image_url ? (
            <img
              src={product.featured_image_url}
              alt={product.title}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
              loading="lazy"
            />
          ) : (
            <div className="h-full w-full bg-platinum" />
          )}
        </div>
      </Link>
      <div className="p-5 border-t border-platinum">
        <div className="font-mono text-[10px] tracking-widest text-graphite mb-2">
          {modelCode}
        </div>
        <Link to={product.link || "#"}>
          <h3 className="font-default text-lg font-bold mb-3 leading-snug hover:text-onyx transition-colors line-clamp-2 min-h-[3.5rem]">
            {product.title}
          </h3>
        </Link>
        <div className="grid grid-cols-3 gap-1 mb-4 pb-4 border-b border-platinum">
          {specs.map((spec, i) => (
            <div key={i} data-index={i} className="font-mono text-[10px] tracking-widest text-graphite truncate">
              {spec.toUpperCase()}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-graphite">PRICE</div>
            <div className="text-xl font-bold text-onyx font-mono">
              ${product.price?.toFixed(2) || "0.00"}
            </div>
          </div>
          <AddToCartButton
            productId={product.id}
            size="icon"
            className="w-auto"
            buttonClassName="bg-onyx text-alabaster rounded-none h-10 w-10 hover:shadow-[4px_4px_0px_0px_var(--accent)]"
            text=""
            showQuantity={false}
          />
        </div>
      </div>
    </article>
  );
}

function ProductsGridConsumer({
  viewMode,
  setViewMode,
}: {
  viewMode: "grid" | "list";
  setViewMode: (mode: "grid" | "list") => void;
}) {
  const {
    products,
    loading,
    error,
    refetch,
    total,
    currentPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    setPage,
  } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(9)].map((_, i) => (
          <div
            key={i}
            data-index={i}
            className="aspect-[3/4] border border-platinum bg-platinum animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-platinum bg-alabaster p-12 text-center">
        <p className="font-mono text-sm tracking-widest text-graphite mb-4">
          CONNECTION ERROR
        </p>
        <Button onClick={refetch} variant="outline" className="font-mono tracking-widest border-onyx">
          RETRY
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="border border-platinum bg-alabaster p-12 text-center">
        <p className="font-default text-lg text-graphite">
          No items found in this collection.
        </p>
      </div>
    );
  }

  const startIdx = (currentPage - 1) * products.length + 1;
  const endIdx = startIdx + products.length - 1;

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-platinum">
        <div className="flex items-center gap-4">
          <div className="flex items-center border border-platinum">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setViewMode("grid")}
              className={`h-9 w-9 rounded-none ${
                viewMode === "grid"
                  ? "bg-onyx text-alabaster"
                  : "hover:bg-platinum"
              }`}
              aria-label="Grid view"
            >
              <Grid3x3 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setViewMode("list")}
              className={`h-9 w-9 rounded-none ${
                viewMode === "list"
                  ? "bg-onyx text-alabaster"
                  : "hover:bg-platinum"
              }`}
              aria-label="List view"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          <div className="font-mono text-xs tracking-widest text-graphite">
            {String(startIdx).padStart(2, "0")} — {String(endIdx).padStart(2, "0")} OF{" "}
            {String(total).padStart(3, "0")}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product, i) => {
          const isSpotlight = (i + 1) % 9 === 0;
          return (
            <ProductCard
              key={product.id}
              product={product}
              index={i}
              spotlight={isSpotlight}
            />
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-0 pt-8 border-t border-platinum">
          <Button
            variant="ghost"
            onClick={prevPage}
            disabled={!hasPrev}
            className="font-mono text-xs tracking-widest border-r border-platinum h-10 px-4"
          >
            ← PREV
          </Button>
          {[...Array(Math.min(totalPages, 7))].map((_, i) => {
            const pageNum = i + 1;
            const isActive = pageNum === currentPage;
            return (
              <Button
                key={pageNum}
                data-index={i}
                variant="ghost"
                onClick={() => setPage(pageNum)}
                className={`font-mono text-xs tracking-widest rounded-none border-r border-platinum h-10 w-10 p-0 ${
                  isActive
                    ? "bg-onyx text-alabaster"
                    : "hover:bg-platinum"
                }`}
              >
                {String(pageNum).padStart(2, "0")}
              </Button>
            );
          })}
          <Button
            variant="ghost"
            onClick={nextPage}
            disabled={!hasNext}
            className="font-mono text-xs tracking-widest h-10 px-4"
          >
            NEXT →
          </Button>
        </div>
      )}
    </div>
  );
}

export default function Shop() {
  const [viewMode, setViewMode] = React.useState<"grid" | "list">("grid");

  return (
    <section data-section-id="2281"
      id="shop-content"
      className="bg-alabaster text-onyx py-24 px-6 lg:px-12"
    >
      <div className="max-w-[1440px] mx-auto">
        <div className="mb-24 pb-12 border-b border-platinum">
          <div className="font-mono text-xs tracking-widest text-onyx mb-4">
            § 01 / CATALOG
          </div>
          <h1 className="font-default text-6xl font-bold tracking-tighter mb-6">
            THE COLLECTION
          </h1>
          <p className="font-default text-xl text-graphite max-w-2xl">
            Explore our latest seasonal arrivals, meticulously crafted for the modern wardrobe.
          </p>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="lg:hidden mb-6">
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full font-mono tracking-widest border-platinum bg-alabaster"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  FILTERS
                </Button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-full sm:max-w-md overflow-y-auto bg-alabaster border-platinum"
              >
                <SheetHeader>
                  <SheetTitle className="font-default text-2xl tracking-tighter">
                    FILTERS
                  </SheetTitle>
                </SheetHeader>
                <div className="p-4">
                  <ProductsFilter />
                </div>
              </SheetContent>
            </Sheet>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <aside className="hidden lg:block lg:col-span-3">
              <div className="sticky top-24 border border-platinum bg-alabaster p-8">
                <div className="flex items-center justify-between mb-8 pb-4 border-b border-platinum">
                  <div className="font-mono text-xs tracking-widest text-onyx">
                    FILTERS
                  </div>
                  <Filter className="h-4 w-4 text-graphite" />
                </div>
                <ProductsFilter />
              </div>
            </aside>

            <div className="lg:col-span-9 space-y-6">
              <div className="flex justify-end">
                <span className="font-mono text-xs tracking-widest text-graphite mr-2 flex items-center">
                  SORT /
                </span>
                <ProductSortDropdown label="" />
              </div>

              <ProductsGridConsumer
                viewMode={viewMode}
                setViewMode={setViewMode}
              />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}